//============================================================================
// Name        : DatePicker.tsx
// Purpose     : Implement Custom Date Picker
// Created     : 2019-10-31
// Author      : Senitron Team
// Copyright   : Copyright 2019 Senitron Corporation. All rights reserved.
//============================================================================

// tslint:disable-next-line:no-unused-expression
/** @jsx jsx */ jsx;
import {css, jsx} from "@emotion/core";
import moment from "moment";
import React, {SyntheticEvent} from "react";

const selectStyle = css`
  height: 30px;
  margin-top: 0px;
  margin-right: 2px;
  &:focus {
    outline: 3px #0c7cd5 solid !important;
  }
`;

interface IDatePickerProps {
    value: string;
    onDateChanged: (val) => void;
    onBlur?: (event: SyntheticEvent) => void;
    readOnly?: boolean;
    maxDate?: string;
    minIsCurrent?: boolean;
}

interface IDatePickerState {
    day: number;
    month: number;
    year: number;
    previousDate?: IDatePickerState;
}

export default class DatePicker extends React.Component<IDatePickerProps, IDatePickerState> {
    constructor(props: IDatePickerProps) {
        super(props);

        // Parse the initial date
        if (this.props.value) {
            const initialValue = moment(this.props.value, "YYYY-MM-DD");
            this.state = {
                day: initialValue.date(),
                month: initialValue.month() + 1,
                year: initialValue.year()
            };
        }
        else {
            this.state = {
                day: 0,
                month: 0,
                year: 0,
                previousDate: {
                    day: 0,
                    month: 0,
                    year: 0
                }
            };
        }
    }

    onMonthChange = (event) => {
        this.setStateOrMax(this.state.year, event.target.value, this.state.day);
    }

    onDayChange = (event) => {
        this.setStateOrMax(this.state.year, this.state.month, event.target.value);
    }

    onYearChange = (event) => {
        this.setStateOrMax(event.target.value, this.state.month, this.state.day);
    }

    setStateOrMax = (year: number, month: number, day: number) => {
        // Need to hold a reference to the current date in case, the change event is cancelled
        // in that case we need this reference to revert back.
        this.setState({ previousDate: this.state });

        const newDate = `${year}-${month}-${day}`;
        const mNewDate = moment(newDate, "YYYY-MM-DD");
        const mMaxDate = moment(this.props.maxDate, "YYYY-MM-DD");

        if (mNewDate > mMaxDate) {
            this.setState({
                day: mMaxDate.date(),
                month: mMaxDate.month() + 1,
                year: mMaxDate.year()
            });
            this.updateValue(mMaxDate.year(), mMaxDate.month() + 1, mMaxDate.date());
        }
        else {
            this.setState({ day, month, year });
            this.updateValue(year, month, day);
        }
    }

    getDays = (): string[] => {
        const dayArray = ["---"];

        try {
            const maxDay = moment(`${this.state.year}-${this.state.month}`, "YYYY-MM").daysInMonth();
            for (let i = 1; i <= maxDay; i++) {
                dayArray.push(i.toString());
            }
        }
        catch (_err) {
            // Left blank
        }
        return dayArray;
    }

    onBlur = (event: SyntheticEvent) => {
        if (this.props.onBlur) {
            this.props.onBlur(event);
        }
    }

    updateValue(year: number, month: number, day: number) {
        const newDate = `${year}-${month}-${day}`;
        this.props.onDateChanged(newDate);
    }

    undoValueUpdate() {
        if (!this.state.previousDate) {
            this.setState({
                year:  0,
                month: 0,
                day:   0
            });
        }
        else {
            this.setState({
                year:  this.state.previousDate.year,
                month: this.state.previousDate.month,
                day:   this.state.previousDate.day
            });
        }
    }

    render() {
        const monthArray = moment.months();
        monthArray.unshift("---");

        const yearArray = [];
        yearArray.push("---");
        if (this.props.minIsCurrent) {
            const startYear = moment().year();
            for (let i = 0; i <= 10; i++) {
                yearArray.push( (startYear + i).toString() );
            }
        }
        else {
            const startYear = moment().year() - 10;
            for (let i = 1; i <= 20; i++) {
                yearArray.push( (startYear + i).toString() );
            }
        }

        return(
            <div css={{ minWidth: 215 }}>
                {/* Fill in years */}
                <select css={selectStyle} name="year" onBlur={this.onBlur} onChange={this.onYearChange} value={this.state.year} disabled={this.props.readOnly}>
                    {yearArray.map((y, key) => <option key={key} value={y}>{y}</option> )}
                </select>

                {/* Fill in months */}
                <select css={selectStyle} name="month" onBlur={this.onBlur} onChange={this.onMonthChange} value={this.state.month} disabled={this.props.readOnly}>
                    {monthArray.map((i, key) => <option key={key} value={key}>{i}</option> )}
                </select>

                {/* Fill in days */}
                <select css={selectStyle} name="day" onBlur={this.onBlur} onChange={this.onDayChange} value={this.state.day} disabled={this.props.readOnly}>
                    {this.getDays().map((i, key) => <option key={key} value={key}>{i}</option> )}
                </select>
            </div>
        );
    }
}
